import { DeepNullablePartial } from '@approvalmax/types';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { backend, domain, stateTree } from 'modules/data';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { amplitudeService } from 'services/amplitude';
import { api } from 'services/api';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { hasChangesState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { completeReview } from '../../../actions';
import { getQBooksRequestVendorTransfer } from '../../../selectors/qbooks';
import { getEditCommands, validateRequest } from '../../../selectors/requestSelectors';
import { isModifiedRequestState } from '../../../states';
import { messages } from './QBOVendorRequest.messages';

export const useSaveAndSubmitVendorMutation = (qboVendor: domain.QBooksVendor, request: domain.QBooksVendorRequest) => {
    const commands = useSelector(getEditCommands);
    const setHasChanges = useSetAtom(hasChangesState);
    const setIsModifiedRequest = useSetAtom(isModifiedRequestState);

    const { mutate, isLoading: isSaving } = useMutation(
        async (
            options: DeepNullablePartial<backend.transfers.RequestEditTransfer> & { needToCompleteReview: boolean }
        ) => {
            const { needToCompleteReview, ...transfer } = options;

            setIsModifiedRequest(true);
            setHasChanges(false);

            await api.requests.edit(transfer);

            if (!commands.saveAndSubmit.hidden && !commands.saveAndSubmit.disabled) {
                await api.requests.publish({
                    requestId: request.id,
                    companyId: request.companyId,
                });
                amplitudeService.sendData('requests: create request', {
                    'request type': domain.IntegrationCode.QBooksVendor.toLocaleLowerCase(),
                });
            }

            if (needToCompleteReview) {
                await completeReview(request.id, request.companyId);
            }

            notificationService.showInfoToast(messages.saveAndSubmitRequestSuccess);

            routingService.push(getPath(Path.request, request.id, request.companyId));
        }
    );

    const saveAndSubmitVendor = useCallback(
        (commentText: string, needToCompleteReview: boolean) => {
            const details = {
                ...request.details,
                ...qboVendor,
            } as domain.QBooksVendorDetails;
            const detailsToSubmit = {
                ...request.details,
                ...qboVendor,
                ...qboVendor.paymentDetails,
            } as domain.QBooksVendorDetails;
            const state = window.ApprovalMax.app.getStore().getState() as stateTree.State;
            const requestWithContact = { ...request, details };
            const errors = validateRequest(state, requestWithContact);

            if (errors.length > 0) {
                errors.forEach((error) => {
                    notificationService.showErrorToast(error);
                });

                return;
            }

            const transfer = getQBooksRequestVendorTransfer(
                window.ApprovalMax.app.getStore().getState() as stateTree.State,
                { ...request, details: detailsToSubmit },
                commentText
            );

            mutate({ ...transfer, needToCompleteReview });
        },
        [mutate, qboVendor, request]
    );

    return { saveAndSubmitVendor, isSaving };
};
